import { useState } from 'react'
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
} from '@mui/material'
import {
  CheckCircle,
  Schedule,
  Warning,
  TrendingUp,
} from '@mui/icons-material'
import { useAuthStore } from '@/store/useAuthStore'
import { useFamilyStore } from '@/store/useFamilyStore'
import { useTaskStore } from '@/store/useTaskStore'
import { useTodaysTasks } from '@/hooks/useTaskOccurrences'
import TaskCard from '@/components/tasks/TaskCard'
import TaskForm from '@/components/tasks/TaskForm'
import TaskCompletionDialog from '@/components/tasks/TaskCompletionDialog'
import type { Task } from '@/types'
import { startOfDay, endOfDay } from 'date-fns'

export default function DashboardPage() {
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [completingTask, setCompletingTask] = useState<Task | null>(null)

  const currentUserId = useAuthStore((state) => state.currentUserId)
  const members = useFamilyStore((state) => state.members)
  const tasks = useTaskStore((state) => state.tasks)
  const completions = useTaskStore((state) => state.completions)

  const currentUser = members.find((m) => m.id === currentUserId)
  const todaysTasks = useTodaysTasks(tasks, currentUserId || '')

  // Calculate today's completions
  const today = new Date()
  const todayStart = startOfDay(today)
  const todayEnd = endOfDay(today)

  const todayCompletions = completions.filter((c) => {
    const completedAt = new Date(c.completed_at)
    return (
      c.completed_by === currentUserId &&
      completedAt >= todayStart &&
      completedAt <= todayEnd
    )
  })

  const completedTaskIds = new Set(todayCompletions.map((c) => c.task_id))
  const pendingTasks = todaysTasks.filter((t) => !completedTaskIds.has(t.id))
  const completionRate =
    todaysTasks.length > 0
      ? (todayCompletions.length / todaysTasks.length) * 100
      : 0

  const handleEdit = (task: Task) => {
    setEditingTask(task)
    setFormOpen(true)
  }

  const handleComplete = (task: Task) => {
    setCompletingTask(task)
  }

  const handleCloseForm = () => {
    setFormOpen(false)
    setEditingTask(null)
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box mb={5}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Welcome back, {currentUser?.name || 'Guest'}!
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            fontSize: '1.125rem',
            fontWeight: 500,
          }}
        >
          {pendingTasks.length === 0
            ? "🎉 All done for today! You're amazing!"
            : `You have ${pendingTasks.length} task${pendingTasks.length === 1 ? '' : 's'} to complete today`}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Schedule sx={{ fontSize: 28 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.95 }}>
                  Today's Tasks
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {todaysTasks.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <CheckCircle sx={{ fontSize: 28 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.95 }}>
                  Completed
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {todayCompletions.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Warning sx={{ fontSize: 28 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.95 }}>
                  Pending
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {pendingTasks.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
              color: 'white',
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <TrendingUp sx={{ fontSize: 28 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.95 }}>
                  Streak
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {currentUser?.streak || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Completion Progress */}
      {todaysTasks.length > 0 && (
        <Card
          sx={{
            mb: 4,
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
            border: '2px solid',
            borderColor: 'primary.main',
          }}
        >
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Today's Progress
              </Typography>
              <Chip
                label={`${Math.round(completionRate)}%`}
                sx={{
                  bgcolor: completionRate === 100 ? 'success.main' : 'primary.main',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  px: 1,
                }}
              />
            </Box>
            <Box
              sx={{
                position: 'relative',
                height: 16,
                bgcolor: 'rgba(0,0,0,0.08)',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${completionRate}%`,
                  background:
                    completionRate === 100
                      ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                      : 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
                  borderRadius: 3,
                  transition: 'width 0.5s ease-in-out',
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                mt: 1.5,
                display: 'block',
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              {todayCompletions.length} of {todaysTasks.length} tasks completed
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Today's Tasks */}
      <Box mb={4}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: 700,
            mb: 2.5,
          }}
        >
          Today's Tasks
        </Typography>

        {todaysTasks.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
                No tasks scheduled for today. Enjoy your free time! 🌟
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {pendingTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                date={today}
                onEdit={handleEdit}
                onComplete={handleComplete}
              />
            ))}

            {completedTaskIds.size > 0 && (
              <>
                <Typography
                  variant="h6"
                  sx={{
                    mt: 4,
                    mb: 2,
                    fontWeight: 700,
                    color: 'success.main',
                  }}
                >
                  Completed Today ✓
                </Typography>
                {todaysTasks
                  .filter((t) => completedTaskIds.has(t.id))
                  .map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      date={today}
                      onEdit={handleEdit}
                    />
                  ))}
              </>
            )}
          </Stack>
        )}
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Your Stats
              </Typography>
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Total Points
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {currentUser?.points || 0}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Current Streak
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {currentUser?.streak || 0} days
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Today's Completion Rate
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {Math.round(completionRate)}%
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Family Leaderboard
              </Typography>
              <Stack spacing={1}>
                {members
                  .sort((a, b) => b.points - a.points)
                  .map((member, index) => (
                    <Box
                      key={member.id}
                      display="flex"
                      alignItems="center"
                      gap={2}
                      p={1}
                      sx={{
                        bgcolor:
                          member.id === currentUserId
                            ? 'action.selected'
                            : 'transparent',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="h6" color="text.secondary" width={24}>
                        #{index + 1}
                      </Typography>
                      <Typography variant="body1" flex={1}>
                        {member.name}
                      </Typography>
                      <Chip
                        label={`${member.points} pts`}
                        size="small"
                        color={index === 0 ? 'primary' : 'default'}
                      />
                    </Box>
                  ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Task Form Dialog */}
      <TaskForm open={formOpen} onClose={handleCloseForm} task={editingTask} />

      {/* Task Completion Dialog */}
      <TaskCompletionDialog
        open={Boolean(completingTask)}
        onClose={() => setCompletingTask(null)}
        task={completingTask}
      />
    </Container>
  )
}
